"use client";
import React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useLocale, useTranslations } from "next-intl";
import { useCreateUser } from "@/lib/hooks/user/useCreateUser";

type FormValues = {
	email?: string;
	password?: string;
	confirmPassword?: string;
	onSubmit: (data?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
	locale?: string;
};

interface SignUpFormProps {
	setActive: any;
}

const SignUpForm = ({ setActive }: SignUpFormProps) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<FormValues>();

	const { createUser, loading } = useCreateUser();
	const locale = useLocale();
	const onSubmit: SubmitHandler<FormValues> = (data) => {
		data.locale = locale;
		createUser(data, reset, setActive);
	};

	const t = useTranslations();

	const inputClass = (hasError: boolean) =>
		`w-full rounded-md border bg-gray-50/60 px-4 py-2.5 text-sm text-gray-900 outline-none transition-colors duration-200 placeholder:text-gray-400 focus:border-[#c07d3e] focus:bg-white focus:ring-1 focus:ring-[#c07d3e]/20 ${
			hasError ? "border-red-400 bg-red-50/40" : "border-gray-200"
		}`;

	return (
		<div className="space-y-5">
			<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
				<div>
					<label
						htmlFor="signup-email"
						className="mb-1.5 block text-left text-[13px] font-medium text-gray-700"
					>
						{t("common.Email")}
					</label>
					<input
						type="email"
						id="signup-email"
						className={inputClass(!!errors.email)}
						placeholder={t("common.Email")}
						{...register("email", { required: true })}
					/>
					{errors.email && <p className="mt-1 text-xs text-red-500">{t("common.Email")} is required</p>}
				</div>

				<div>
					<label
						htmlFor="signup-password"
						className="mb-1.5 block text-left text-[13px] font-medium text-gray-700"
					>
						{t("common.Password")}
					</label>
					<input
						type="password"
						id="signup-password"
						className={inputClass(!!errors.password)}
						placeholder="••••••••"
						{...register("password", {
							required: true,
						})}
					/>
					{errors.password && <p className="mt-1 text-xs text-red-500">{t("common.Password")} is required</p>}
				</div>

				<div>
					<label
						htmlFor="signup-confirm-password"
						className="mb-1.5 block text-left text-[13px] font-medium text-gray-700"
					>
						{t("message.abe0432e8d804e4cd748b0993c2626be5413")}
					</label>
					<input
						type="password"
						id="signup-confirm-password"
						className={inputClass(!!errors.confirmPassword)}
						placeholder="••••••••"
						{...register("confirmPassword", { required: true })}
					/>
					{errors.confirmPassword && (
						<p className="mt-1 text-xs text-red-500">
							{t("message.abe0432e8d804e4cd748b0993c2626be5413")} is required
						</p>
					)}
				</div>

				<button
					type="submit"
					disabled={loading}
					className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2.5 rounded-md bg-[#ff9f0a] px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-[#e68d00] focus:outline-none focus:ring-2 focus:ring-[#ff9f0a]/25 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{loading && (
						<span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
					)}
					{loading ? t("message.7f9e518a7a1d1e4bc3e8990bed1d9be4d404") + "..." : t("common.Sign_Up")}
				</button>
			</form>
		</div>
	);
};

export default SignUpForm;
